'use client'

import { useEffect, useRef } from 'react'
import { useUser } from '@clerk/nextjs'
import { useMutation } from 'convex/react'
import { ConvexError } from 'convex/values'
import { api } from '@/convex/_generated/api'
import { getFirstTouch, getReferralCode } from '@/lib/analytics/attribution'
import { EMAIL_BLOCKLIST_ERROR_CODE } from '@/lib/email-blocklist'
import { deviceFingerprint } from '@/lib/abuse/device-fingerprint'

/**
 * UserInitializer component
 *
 * Ensures that every logged-in user has a record in the Convex users table.
 * This component runs globally on every page, so new users are automatically
 * created with their initial credits when they first log in.
 */
export function UserInitializer() {
  const { user } = useUser()
  const userId = user?.id
  const getOrCreateUser = useMutation(api.users.getOrCreateUser)
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Only run once per session
    if (!userId || hasInitialized.current) {
      return
    }

    // Mark as initialized to prevent duplicate calls
    hasInitialized.current = true

    // Call getOrCreateUser to ensure user record exists
    // This will create the user with 75 free credits if they don't exist
    getOrCreateUser({ userId, acquisition: getFirstTouch(), referralCode: getReferralCode() })
      .then((result) => {
        // First-ever sign-in for this user. The welcome email used to fire
        // here, but that's before any plan exists — it could never state
        // plan/price/trial-end (2026-08-31 tester audit). It's now sent from
        // the Stripe webhook once checkout actually completes and that
        // information exists (see checkout.session.completed's
        // trialStartedEmailHtml, which reuses this same markWelcomeEmailSent
        // one-time claim so it still only ever sends once).
        if (result?.isNewUser) {
          fetch('/api/hubspot/sync-user', { method: 'POST' }).catch(() => {})
          // ENG-34 (#216): the IP and country can only be read server-side, so
          // hand the request to a route that reads them from its own headers.
          // The device string is the only thing the client contributes, and it
          // is hashed with the deployment salt before anything is stored. A
          // no-op unless IDENTITY_TRACES_ENABLED=1; failures are swallowed —
          // abuse telemetry must never break a first session.
          fetch('/api/identity/signup-trace', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ device: deviceFingerprint() }),
          }).catch(() => {})
        }
      })
      .catch((error) => {
        // ENG-12 / #213: the server refused to register this email domain.
        // Hand off to the readable-error page (which also removes the Clerk
        // account) and do NOT retry — the answer will not change.
        if (error instanceof ConvexError && (error.data as { code?: string } | undefined)?.code === EMAIL_BLOCKLIST_ERROR_CODE) {
          if (window.location.pathname !== '/sign-up/blocked') window.location.replace('/sign-up/blocked')
          return
        }
        console.error('[User Initializer] ❌ Failed to initialize user:', error)
        // Reset flag on error so we can retry
        hasInitialized.current = false
      })
  }, [userId, getOrCreateUser])

  // This component doesn't render anything
  return null
}
